import { createContext, useContext, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const requestConfirm = useCallback((message, onConfirm, options = {}) => {
    setState({ message, onConfirm, ...options });
  }, []);
  const close = () => setState(null);
  const handleYes = () => {
    if (state?.onConfirm) state.onConfirm();
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={requestConfirm}>
      {children}
      {state && (
        <ConfirmModal
          title={state.title}
          message={state.message}
          confirmLabel={state.confirmLabel}
          cancelLabel={state.cancelLabel}
          tone={state.tone}
          onYes={handleYes}
          onNo={close}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}

function ConfirmModal({
  title = "Confirm deletion",
  message,
  confirmLabel = "Yes, delete",
  cancelLabel = "No, keep it",
  tone = "danger",
  onYes,
  onNo,
}) {
  const color = tone === "danger" ? "var(--danger)" : "var(--mint)";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, display: "grid", placeItems: "center", padding: 16, background: "rgba(2,5,9,0.75)", backdropFilter: "blur(4px)" }} data-testid="confirm-modal">
      <div style={{ background: "var(--panel)", border: `1px solid ${color}55`, borderRadius: 14, padding: 20, width: "min(400px, 96vw)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ padding: 8, borderRadius: 6, background: `${color}14`, border: `1px solid ${color}55`, flexShrink: 0 }}>
            <AlertTriangle size={16} style={{ color }} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{message}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button
            onClick={onNo}
            data-testid="confirm-no"
            style={{
              flex: 1, padding: "10px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase", fontFamily: "Space Grotesk", cursor: "pointer",
              background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onYes}
            data-testid="confirm-yes"
            style={{
              flex: 1, padding: "10px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase", fontFamily: "Space Grotesk", cursor: "pointer",
              background: `${color}1a`, color, border: `1px solid ${color}`, borderRadius: 8,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
